// Endpoint modules grouped by resource. Each function returns a fully typed
// promise — schema validation is opt-in per call (set inline below) so we
// catch backend drift the moment it happens.

import { z } from 'zod';
import { apiRequest } from './client';
import {
  AuditEntrySchema,
  ChatApplyOutSchema,
  ChatMessageSchema,
  ChatSendOutSchema,
  ChatSessionSchema,
  ComponentSchema,
  HostingRecHistorySchema,
  HostingRecResponseSchema,
  IdeaSchema,
  InitiativeSchema,
  InviteSchema,
  NotificationSchema,
  OrgSystemSchema,
  ProjectProgressSchema,
  ProjectSchema,
  ReleaseSchema,
  SearchResultsSchema,
  SentLinkSchema,
  TicketSchema,
  AgentConfigSchema,
  MeSchema,
  ProjectFileLatestSchema,
  ProjectFileSchema,
  ProjectRepositorySchema,
  UserSchema,
  type AgentConfig,
  type AuditEntry,
  type Me,
  type ProjectFile,
  type ProjectFileLatest,
  type ProjectRepository,
  type ChatApplyOut,
  type ChatMessage,
  type ChatSendOut,
  type ChatSession,
  type Component,
  type HostingRecHistory,
  type HostingRecResponse,
  type Idea,
  type Initiative,
  type Invite,
  type Notification,
  type OrgSystem,
  type Project,
  type ProjectProgress,
  type Release,
  type SearchResults,
  type SentLink,
  type Ticket,
  type User,
} from '@/types/api';
import type { GateOutcome } from '@/theme/phase';

export interface ProjectCreate {
  name: string;
  owner_id?: number;
  sponsor_id?: number;
  developer_id?: number;
  goal?: string;
  business_case?: string;
  user_base_type?: string;
  user_base_size?: number;
  involves_phi_pii?: boolean;
  phi_pii_description?: string;
  target_go_live?: string;
  proposal_date?: string;
  sharepoint_url?: string;
  priority_score?: number | null;
  recommended_hosting?: string;
  hosting_rationale?: string;
  tech?: ProjectTechUpdate;
}

// ProjectTechIn-equivalent for the PATCH path. Arrays are sent as-is; the
// edit modal renders them as comma-separated strings and parses back to
// arrays at save time.
export interface ProjectTechUpdate {
  languages?: string[];
  external_systems?: string[];
  internal_systems?: string[];
  hosting?: string | null;
  auth_method?: string | null;
  secrets_method?: string | null;
  data_storage?: string | null;
  cost_estimate?: string | null;
}

export interface ProjectUpdate {
  name?: string;
  owner_id?: number | null;
  sponsor_id?: number | null;
  developer_id?: number | null;
  goal?: string;
  business_case?: string;
  user_base_type?: string;
  user_base_size?: number;
  involves_phi_pii?: boolean;
  phi_pii_description?: string;
  target_go_live?: string;
  actual_go_live?: string;
  recommended_hosting?: string;
  hosting_rationale?: string;
  hosting_override_reason?: string;
  sharepoint_url?: string;
  status?: string;
  priority_score?: number | null;
  tech?: ProjectTechUpdate;
}

export interface IdeaCreate {
  title: string;
  description?: string;
  owner_id?: number;
}

export interface IdeaUpdate {
  title?: string;
  description?: string;
  owner_id?: number | null;
  status?: string;
}

export interface InitiativeUpdate {
  name?: string;
  description?: string;
  target_completion?: string;
  status?: string;
  color?: string;
  project_ids?: number[];
}

export interface InitiativeCreate {
  name: string;
  description?: string;
  target_completion?: string;
  status?: string;
  color?: string;
  project_id?: number;
  project_ids?: number[];
}

export interface AuditFilter {
  entity_type?: string;
  entity_id?: number;
  user_id?: number;
  action_prefix?: string;
  since?: string;
  limit?: number;
}

export interface RegisterPayload {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  function?: string | null;
}

export const auth = {
  // Passwordless: email only. A 404 means the email isn't on the roster yet —
  // the login page catches that and offers self-registration.
  login: (email: string): Promise<User> =>
    apiRequest('/api/auth/login', {
      method: 'POST',
      body: { email },
      schema: UserSchema,
      skipAuthRedirect: true,
    }),

  register: (payload: RegisterPayload): Promise<User> =>
    apiRequest('/api/auth/register', {
      method: 'POST',
      body: payload,
      schema: UserSchema,
      skipAuthRedirect: true,
    }),

  logout: (): Promise<unknown> =>
    apiRequest('/api/auth/logout', { method: 'POST' }),

  // /api/auth/me returns MeOut — UserOut + personal_context. Cast to Me on
  // the client side via MeSchema. Stale-time on the useMe hook keeps this
  // cheap to call.
  me: (): Promise<Me> =>
    apiRequest('/api/auth/me', { schema: MeSchema, skipAuthRedirect: true }),

  updatePersonalContext: (personal_context: string): Promise<Me> =>
    apiRequest('/api/auth/me/personal-context', {
      method: 'PATCH',
      body: { personal_context },
      schema: MeSchema,
    }),
};

export interface UserUpdate {
  name?: string;
  role?: string;
  function?: string | null;
  is_active?: boolean;
}

export const users = {
  list: (): Promise<User[]> =>
    apiRequest('/api/users', { schema: z.array(UserSchema) }),

  update: (id: number, body: UserUpdate): Promise<User> =>
    apiRequest(`/api/users/${id}`, {
      method: 'PATCH',
      body,
      schema: UserSchema,
    }),

  sendReset: (id: number): Promise<SentLink> =>
    apiRequest(`/api/users/${id}/send-reset`, {
      method: 'POST',
      schema: SentLinkSchema,
    }),
};

export interface InviteCreate {
  email: string;
  name?: string;
  role?: string;
}

export const invites = {
  list: (): Promise<Invite[]> =>
    apiRequest('/api/invites', { schema: z.array(InviteSchema) }),

  create: (body: InviteCreate): Promise<SentLink> =>
    apiRequest('/api/invites', {
      method: 'POST',
      body,
      schema: SentLinkSchema,
    }),

  resend: (id: number): Promise<SentLink> =>
    apiRequest(`/api/invites/${id}/resend`, {
      method: 'POST',
      schema: SentLinkSchema,
    }),

  revoke: (id: number): Promise<unknown> =>
    apiRequest(`/api/invites/${id}`, { method: 'DELETE' }),
};

export interface OrgSystemCreate {
  name: string;
  category?: string;
  notes?: string;
}

export const orgSystems = {
  list: (): Promise<OrgSystem[]> =>
    apiRequest('/api/org_systems', { schema: z.array(OrgSystemSchema) }),

  create: (body: OrgSystemCreate): Promise<OrgSystem> =>
    apiRequest('/api/org_systems', {
      method: 'POST',
      body,
      schema: OrgSystemSchema,
    }),

  remove: (id: number): Promise<unknown> =>
    apiRequest(`/api/org_systems/${id}`, { method: 'DELETE' }),
};

export const notifications = {
  list: (): Promise<Notification[]> =>
    apiRequest('/api/notifications', { schema: z.array(NotificationSchema) }),

  markRead: (id: number): Promise<unknown> =>
    apiRequest(`/api/notifications/${id}/read`, { method: 'POST' }),
};

// Admin-only agent configuration. Each agent (default, intake) exposes
// its editable system prompt and a read-only list of wired tools.
export const agents = {
  list: (): Promise<AgentConfig[]> =>
    apiRequest('/api/agents', { schema: z.array(AgentConfigSchema) }),

  get: (name: string): Promise<AgentConfig> =>
    apiRequest(`/api/agents/${name}`, { schema: AgentConfigSchema }),

  updatePrompt: (name: string, system_prompt: string): Promise<AgentConfig> =>
    apiRequest(`/api/agents/${name}`, {
      method: 'PATCH',
      body: { system_prompt },
      schema: AgentConfigSchema,
    }),
};

export const projects = {
  list: (): Promise<Project[]> =>
    apiRequest('/api/projects', { schema: z.array(ProjectSchema) }),

  get: (id: number): Promise<Project> =>
    apiRequest(`/api/projects/${id}`, { schema: ProjectSchema }),

  create: (body: ProjectCreate): Promise<Project> =>
    apiRequest('/api/projects', {
      method: 'POST',
      body,
      schema: ProjectSchema,
    }),

  update: (id: number, body: ProjectUpdate): Promise<Project> =>
    apiRequest(`/api/projects/${id}`, {
      method: 'PATCH',
      body,
      schema: ProjectSchema,
    }),

  reviewGate: (
    projectId: number,
    gateId: number,
    body: { outcome: GateOutcome; notes?: string; reviewer_id?: number },
  ): Promise<Project> =>
    apiRequest(`/api/projects/${projectId}/gates/${gateId}`, {
      method: 'POST',
      body,
      schema: ProjectSchema,
    }),

  progress: (id: number): Promise<ProjectProgress[]> =>
    apiRequest(`/api/projects/${id}/progress`, {
      schema: z.array(ProjectProgressSchema),
    }),

  addProgress: (
    id: number,
    body: { note: string; entry_date?: string },
  ): Promise<ProjectProgress> =>
    apiRequest(`/api/projects/${id}/progress`, {
      method: 'POST',
      body,
      schema: ProjectProgressSchema,
    }),

  // Archive workflow — see apm/app/routes/projects.py:archive_project.
  // Archive is reversible (unarchive); demote-to-idea + delete are not.
  archive: (id: number): Promise<Project> =>
    apiRequest(`/api/projects/${id}/archive`, {
      method: 'POST',
      schema: ProjectSchema,
    }),

  unarchive: (id: number): Promise<Project> =>
    apiRequest(`/api/projects/${id}/unarchive`, {
      method: 'POST',
      schema: ProjectSchema,
    }),

  // Hard-deletes the project (cascading to gates / tickets / releases)
  // and returns the new Idea row holding the kernel of the work.
  demoteToIdea: (id: number): Promise<Idea> =>
    apiRequest(`/api/projects/${id}/demote-to-idea`, {
      method: 'POST',
      schema: IdeaSchema,
    }),

  // Permanent delete. Backend enforces status='Archived' as a safety
  // gate — call archive() first, then this.
  remove: (id: number): Promise<unknown> =>
    apiRequest(`/api/projects/${id}`, { method: 'DELETE' }),
};

// Per-project file storage. Backend routes are nested under
// /api/projects/{project_id}/files. Markdown content for small files is
// fetched as plain text rather than the binary download endpoint so we
// can render it inline.
export const files = {
  list: (projectId: number): Promise<ProjectFileLatest[]> =>
    apiRequest(`/api/projects/${projectId}/files`, {
      schema: z.array(ProjectFileLatestSchema),
    }),

  versions: (projectId: number, filename: string): Promise<ProjectFile[]> =>
    apiRequest(
      `/api/projects/${projectId}/files/${encodeURIComponent(filename)}/versions`,
      { schema: z.array(ProjectFileSchema) },
    ),

  // Returns the raw file content as text. The backend's download endpoint
  // serves bytes; for markdown we fetch + decode as UTF-8 client-side.
  // Throws for non-text content types so callers don't accidentally show
  // gibberish for binaries — they should use downloadUrl instead.
  getText: async (projectId: number, fileId: number): Promise<string> => {
    const res = await fetch(`/api/projects/${projectId}/files/${fileId}/download`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  },

  // URL the user can navigate to / window.open for a binary download.
  // The backend sends Content-Disposition: attachment for non-text types.
  downloadUrl: (projectId: number, fileId: number): string =>
    `/api/projects/${projectId}/files/${fileId}/download`,

  // Upload a file (multipart). Used for small text/markdown uploads from
  // the Files tab. Doesn't go through apiRequest because that one
  // assumes JSON; we hit fetch directly with FormData so the boundary
  // header is set automatically.
  upload: async (
    projectId: number,
    file: File,
    description?: string,
  ): Promise<ProjectFile> => {
    const fd = new FormData();
    fd.append('file', file);
    if (description) fd.append('description', description);
    const res = await fetch(`/api/projects/${projectId}/files`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(body || `HTTP ${res.status}`);
    }
    return ProjectFileSchema.parse(await res.json());
  },
};

// Tier-2 file storage pointers per project — GitHub, SharePoint, local,
// deployed URLs. See apm/app/routes/repositories.py for the route surface
// and apm/app/models.py:ProjectRepository for the schema.
export interface ProjectRepositoryCreate {
  kind: string;
  url: string;
  label?: string;
  is_primary?: boolean;
  notes?: string;
}

export interface ProjectRepositoryUpdate {
  kind?: string;
  url?: string;
  label?: string;
  is_primary?: boolean;
  notes?: string;
}

export const repositories = {
  list: (projectId: number): Promise<ProjectRepository[]> =>
    apiRequest(`/api/projects/${projectId}/repositories`, {
      schema: z.array(ProjectRepositorySchema),
    }),

  create: (projectId: number, body: ProjectRepositoryCreate): Promise<ProjectRepository> =>
    apiRequest(`/api/projects/${projectId}/repositories`, {
      method: 'POST',
      body,
      schema: ProjectRepositorySchema,
    }),

  update: (
    projectId: number,
    repoId: number,
    body: ProjectRepositoryUpdate,
  ): Promise<ProjectRepository> =>
    apiRequest(`/api/projects/${projectId}/repositories/${repoId}`, {
      method: 'PATCH',
      body,
      schema: ProjectRepositorySchema,
    }),

  // Single-shot promote — clears the previous primary atomically on the
  // backend so the UI never has to coordinate two PATCH calls.
  setPrimary: (projectId: number, repoId: number): Promise<ProjectRepository> =>
    apiRequest(`/api/projects/${projectId}/repositories/${repoId}/set-primary`, {
      method: 'POST',
      schema: ProjectRepositorySchema,
    }),

  remove: (projectId: number, repoId: number): Promise<unknown> =>
    apiRequest(`/api/projects/${projectId}/repositories/${repoId}`, {
      method: 'DELETE',
    }),
};

export interface AiStatus {
  claude_configured: boolean;
  claude_working: boolean;
  model: string;
  error: string | null;
}

export const ai = {
  status: (): Promise<AiStatus> =>
    apiRequest('/api/ai/status') as Promise<AiStatus>,

  runHostingForProject: (projectId: number): Promise<HostingRecResponse> =>
    apiRequest(`/api/ai/projects/${projectId}/hosting-recommendation`, {
      method: 'POST',
      schema: HostingRecResponseSchema,
    }),

  hostingHistory: (projectId: number): Promise<HostingRecHistory[]> =>
    apiRequest(`/api/ai/projects/${projectId}/hosting-history`, {
      schema: z.array(HostingRecHistorySchema),
    }),
};

export const initiatives = {
  list: (params?: { project_id?: number }): Promise<Initiative[]> =>
    apiRequest('/api/initiatives', {
      query: params,
      schema: z.array(InitiativeSchema),
    }),

  get: (id: number): Promise<Initiative> =>
    apiRequest(`/api/initiatives/${id}`, { schema: InitiativeSchema }),

  create: (body: InitiativeCreate): Promise<Initiative> =>
    apiRequest('/api/initiatives', {
      method: 'POST',
      body,
      schema: InitiativeSchema,
    }),

  update: (id: number, body: InitiativeUpdate): Promise<Initiative> =>
    apiRequest(`/api/initiatives/${id}`, {
      method: 'PATCH',
      body,
      schema: InitiativeSchema,
    }),

  attachProject: (initiativeId: number, projectId: number): Promise<Initiative> =>
    apiRequest(`/api/initiatives/${initiativeId}/projects/${projectId}`, {
      method: 'POST',
      schema: InitiativeSchema,
    }),

  detachProject: (initiativeId: number, projectId: number): Promise<Initiative> =>
    apiRequest(`/api/initiatives/${initiativeId}/projects/${projectId}`, {
      method: 'DELETE',
      schema: InitiativeSchema,
    }),
};

export const components = {
  list: (): Promise<Component[]> =>
    apiRequest('/api/components', { schema: z.array(ComponentSchema) }),

  get: (id: number): Promise<Component> =>
    apiRequest(`/api/components/${id}`, { schema: ComponentSchema }),
};

export const ideas = {
  list: (): Promise<Idea[]> =>
    apiRequest('/api/ideas', { schema: z.array(IdeaSchema) }),

  get: (id: number): Promise<Idea> =>
    apiRequest(`/api/ideas/${id}`, { schema: IdeaSchema }),

  create: (body: IdeaCreate): Promise<Idea> =>
    apiRequest('/api/ideas', { method: 'POST', body, schema: IdeaSchema }),

  update: (id: number, body: IdeaUpdate): Promise<Idea> =>
    apiRequest(`/api/ideas/${id}`, {
      method: 'PATCH',
      body,
      schema: IdeaSchema,
    }),

  remove: (id: number): Promise<unknown> =>
    apiRequest(`/api/ideas/${id}`, { method: 'DELETE' }),
};

export const audit = {
  list: (filter?: AuditFilter): Promise<AuditEntry[]> =>
    apiRequest('/api/audit-log', {
      query: filter as Record<string, string | number | boolean | undefined | null> | undefined,
      schema: z.array(AuditEntrySchema),
    }),
};

export const search = {
  query: (q: string, perGroup = 8): Promise<SearchResults> =>
    apiRequest('/api/search', {
      query: { q, per_group: perGroup },
      schema: SearchResultsSchema,
    }),
};

export interface ChatSendBody {
  message: string;
  page_scope_type?: string;
  page_scope_id?: number;
  // Sent only from /projects/new: a snapshot of the current intake form so
  // the agent sees the user's edits and doesn't re-ask. Free-form JSON.
  intake_form_state?: Record<string, unknown>;
}

export const chat = {
  session: (): Promise<ChatSession> =>
    apiRequest('/api/chat/session', { schema: ChatSessionSchema }),

  send: (body: ChatSendBody): Promise<ChatSendOut> =>
    apiRequest('/api/chat/send', {
      method: 'POST',
      body,
      schema: ChatSendOutSchema,
    }),

  apply: (message_id: number): Promise<ChatApplyOut> =>
    apiRequest('/api/chat/apply', {
      method: 'POST',
      body: { message_id },
      schema: ChatApplyOutSchema,
    }),

  cancel: (message_id: number): Promise<ChatMessage> =>
    apiRequest('/api/chat/cancel', {
      method: 'POST',
      body: { message_id },
      schema: ChatMessageSchema,
    }),

  clear: (): Promise<ChatSession> =>
    apiRequest('/api/chat/clear', {
      method: 'POST',
      schema: ChatSessionSchema,
    }),

  // `from_idea_id` is the structured fast-forward for idea-to-project
  // promotion: backend pre-fills the intake form (name + goal) from the
  // idea and synthesizes the opening turns so the agent lands in Phase B.
  // Use this instead of `seed` when promoting an idea — wins if both set.
  setMode: (
    mode: 'default' | 'intake',
    opts?: { clear?: boolean; seed?: string; from_idea_id?: number },
  ): Promise<ChatSession> =>
    apiRequest('/api/chat/mode', {
      method: 'POST',
      body: {
        mode,
        clear: opts?.clear ?? true,
        seed: opts?.seed,
        from_idea_id: opts?.from_idea_id,
      },
      schema: ChatSessionSchema,
    }),
};

export const releases = {
  list: (params?: { project_id?: number }): Promise<Release[]> =>
    apiRequest('/api/releases', {
      query: params,
      schema: z.array(ReleaseSchema),
    }),

  get: (id: number): Promise<Release> =>
    apiRequest(`/api/releases/${id}`, { schema: ReleaseSchema }),
};

export interface TicketCreate {
  project_id: number;
  type: string;
  title: string;
  description?: string;
  priority?: string;
  status?: string;
  effort?: string;
  due_date?: string;
  assignee_id?: number | null;
  initiative_id?: number | null;
  release_id?: number | null;
  parent_ticket_id?: number | null;
}

export interface TicketUpdate {
  type?: string;
  title?: string;
  description?: string;
  status?: string;
  priority?: string;
  effort?: string;
  due_date?: string;
  assignee_id?: number | null;
  initiative_id?: number | null;
  release_id?: number | null;
  parent_ticket_id?: number | null;
  owner_response?: string;
}

export const tickets = {
  list: (params?: {
    project_id?: number;
    status?: string;
    initiative_id?: number;
    release_id?: number;
    assignee_id?: number;
  }): Promise<Ticket[]> =>
    apiRequest('/api/tickets', {
      query: params,
      schema: z.array(TicketSchema),
    }),

  get: (id: number): Promise<Ticket> =>
    apiRequest(`/api/tickets/${id}`, { schema: TicketSchema }),

  create: (body: TicketCreate): Promise<Ticket> =>
    apiRequest('/api/tickets', {
      method: 'POST',
      body,
      schema: TicketSchema,
    }),

  update: (id: number, body: TicketUpdate): Promise<Ticket> =>
    apiRequest(`/api/tickets/${id}`, {
      method: 'PATCH',
      body,
      schema: TicketSchema,
    }),

  // Stream a Claude-Code-ready system prompt from the ticket + project
  // context. The UI accumulates chunks into the owner_response field so
  // the user sees the response generate in real time — a more "magical"
  // feel than a 5–10 second wait for a fully-formed reply.
  //
  // Resolves when the stream completes. `used_fallback` is true if the
  // backend served the template-only response (ANTHROPIC_API_KEY missing)
  // instead of a live Claude stream — still useful, but flag it in the UI.
  generateResponseStream: async (
    id: number,
    onChunk: (delta: string) => void,
    signal?: AbortSignal,
  ): Promise<{ used_fallback: boolean; aborted: boolean }> => {
    let res: Response;
    try {
      res = await fetch(`/api/tickets/${id}/generate-response`, {
        method: 'POST',
        credentials: 'include',
        signal,
      });
    } catch (e) {
      // fetch() rejects with AbortError if the signal fired before headers
      // came back. Surface as aborted; whatever the caller has so far is
      // their result.
      if (signal?.aborted) return { used_fallback: false, aborted: true };
      throw e;
    }
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(body || `HTTP ${res.status}`);
    }
    const usedFallback = res.headers.get('X-Atlas-Used-Fallback') === 'true';
    const reader = res.body?.getReader();
    if (!reader) {
      const full = await res.text();
      if (full) onChunk(full);
      return { used_fallback: usedFallback, aborted: false };
    }
    const decoder = new TextDecoder('utf-8');
    let aborted = false;
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        if (chunk) onChunk(chunk);
      }
      // Flush any trailing bytes the decoder may have buffered.
      const tail = decoder.decode();
      if (tail) onChunk(tail);
    } catch (e) {
      // Abort mid-stream: signal cancellation, swallow the error so the
      // mutation succeeds with whatever the caller has accumulated.
      if (signal?.aborted) {
        aborted = true;
        // Best-effort: release the reader so the underlying connection
        // can be reclaimed even if cancel() is unavailable.
        try { reader.cancel(); } catch { /* noop */ }
      } else {
        throw e;
      }
    }
    return { used_fallback: usedFallback, aborted };
  },
};
